import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../../components/ui/select';
import { Settings, Save, RefreshCw, Grid, Filter, Target, Loader2 } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { useLimitsData, useUpdateStatusConfiguration } from '../../../../../redux/WeldData/hooks/useLimitsData';
import { useProjectManager } from '../../../../../redux/WeldData/hooks/useProjectManager';
import { useWeldData } from '../../../../../redux/WeldData/hooks/useWeldData';
import { Notification } from '../../../../../components/ui/custom-notification';
import { ValidationErrorModal } from './ValidationErrorModal';
import { logStatusConfigurationUpdated } from '../../../utils/productionAudit';
import type { ValidationError } from './ValidationErrorModal';
import {
  convertStatusConfigFromBase,
  convertStatusConfigToBase,
  extractStatusConfigUnits,
  formatDisplayDecimals,
  getDefaultStatusConfigUnits,
  getStatusConfigParameterLabel,
  getStatusConfigParameterUnit,
  isStatusConfigUnitParameter,
  StatusConfigParameterKey,
} from './statusConfigurationUnits';
import { sortPassGroupsForStatusConfig } from './statusConfigurationPassOrder';
import { getReportColumns } from '../../../../../services/reportColumnsApi';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { ManualStatusConfiguration, type ManualStatusConfigurationHandle } from './ManualStatusConfiguration';
import { selectStatusJobOptionsToSave } from './statusConfigurationMultiJobSave';
import { ManualWeldingSettingsFields } from './ManualWeldingSettingsFields';
import {
  buildAllStatusJobOptionsFromLimits,
  getStatusJobConfigurationKey,
  isManualStatusJob,
  resolveStatusJobOption,
} from './statusConfigurationManualJob';
import { getManualStatusWeldingMachineOptions } from './manualJobNumberFormat';

interface StatusConfigurationProps {
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  isEditing?: boolean;
  setIsEditing?: (editing: boolean) => void;
  onSave?: () => void;
  onSaveComplete?: () => void;
  triggerSave?: boolean;
  onValidationErrorsChange?: (hasErrors: boolean, errors?: any[]) => void;
  onValidationFailure?: () => void;
  onJobPipeInfoChange?: (jobNumber: string, pipeName: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  isModalOpen?: boolean;
  canEdit?: boolean;
  unitsRefreshToken?: number;
  onEnabledParameterKeysChange?: (enabled: Record<string, boolean>) => void;
}

interface StatusParameter {
  id: string;
  parameter: string;
  enabled: boolean;
  [key: string]: string | boolean; // Allow dynamic properties for any pass group
}

interface LimitsDataItem {
  id: number;
  project_id: number;
  project_name: string;
  welding_system: string;
  job_number: string;
  pipe_name?: string;
  zone_name: string;
  pass_name: string;
  pass_group_name: string;
  current_max: number;
  current_min: number;
  volts_max: number;
  volts_min: number;
  wire_speed_max: number;
  wire_speed_min: number;
  travel_speed_max: number;
  travel_speed_min: number;
  oscillation_width_max: number;
  oscillation_width_min: number;
  true_energy_max: number | null;
  true_energy_min: number | null;
  heat_max: number | null;
  heat_min: number | null;
}

interface WeldDetail {
  id: number;
  project_id: number;
  project_name: string;
  welding_system: string;
  job_number: string;
  slope_in_time: number;
  slope_out_time: number;
  statusCalculationLevel: string;
}

export const StatusConfiguration: React.FC<StatusConfigurationProps> = ({ project, selectedPipeSize, selectedPipe, isEditing: externalIsEditing, setIsEditing: externalSetIsEditing, onSave, onSaveComplete, triggerSave, onValidationErrorsChange, onValidationFailure, onJobPipeInfoChange, onDirtyChange, isModalOpen, canEdit = true, unitsRefreshToken = 0, onEnabledParameterKeysChange }) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = externalSetIsEditing || setInternalIsEditing;
  const projectDefaultUnit = project?.project?.unit || 'in';
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [parameterUnits, setParameterUnits] = useState<Record<StatusConfigParameterKey, string>>(() =>
    getDefaultStatusConfigUnits(projectDefaultUnit)
  );

  const getDynamicParameters = (units = parameterUnits): StatusParameter[] => [
    { id: '1', parameter: getStatusConfigParameterLabel('Current', units), enabled: true },
    { id: '2', parameter: getStatusConfigParameterLabel('Volts', units), enabled: true },
    { id: '3', parameter: getStatusConfigParameterLabel('Wire Speed', units), enabled: true },
    { id: '4', parameter: getStatusConfigParameterLabel('Travel Speed', units), enabled: true },
    { id: '5', parameter: getStatusConfigParameterLabel('Oscillation Width', units), enabled: true },
    { id: '6', parameter: getStatusConfigParameterLabel('Heat', units), enabled: true },
    { id: '7', parameter: getStatusConfigParameterLabel('True Energy', units), enabled: true },
  ];

  // Helper function to map parameter names to API keys
  const getParameterApiKey = (parameterName: string): string | null => {
    if (parameterName.includes('Current')) return 'current';
    if (parameterName.includes('Volts')) return 'volts';
    if (parameterName.includes('Wire Speed')) return 'wire_speed';
    if (parameterName.includes('Travel Speed')) return 'travel_speed';
    if (parameterName.includes('Oscillation Width')) return 'oscillation_width';
    if (parameterName.includes('Heat')) return 'heat';
    if (parameterName.includes('True Energy')) return 'true_energy';
    return null;
  };

  // Function to get parameter key from dynamic parameter name
  const getParameterKey = (parameterName: string): string => {
    // Use the helper function and convert null to empty string for backward compatibility
    return getParameterApiKey(parameterName) || '';
  };

  const convertValueForDisplay = (value: number, parameter: string, units = parameterUnits): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;
    if (parameter.includes('Current') || parameter.includes('Volts')) return value;

    const paramKey = getParameterApiKey(parameter);
    if (!paramKey || !isStatusConfigUnitParameter(paramKey)) return value;
    return convertStatusConfigFromBase(value, paramKey, units[paramKey]);
  };

  const convertValueToInches = (value: number, parameter: string, units = parameterUnits): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;
    if (parameter.includes('Current') || parameter.includes('Volts')) return value;

    const paramKey = getParameterApiKey(parameter);
    if (!paramKey || !isStatusConfigUnitParameter(paramKey)) return value;
    return convertStatusConfigToBase(value, paramKey, units[paramKey]);
  };

  const formatValueForDisplay = (
    value: number,
    parameter: string,
    units: Record<StatusConfigParameterKey, string> = parameterUnits
  ): string => {
    if (typeof value !== 'number' || isNaN(value)) return '';
    const convertedValue = convertValueForDisplay(value, parameter, units);
    return formatDisplayDecimals(convertedValue, parameter);
  };

  const formatValueForAPI = (value: number, parameter: string, units = parameterUnits): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;
    return convertValueToInches(value, parameter, units);
  };

  const applyUnitsToParameters = (
    prev: StatusParameter[],
    oldUnits: Record<StatusConfigParameterKey, string>,
    newUnits: Record<StatusConfigParameterKey, string>
  ): StatusParameter[] =>
    prev.map((param) => {
      const paramKey = getParameterApiKey(param.parameter);
      const updated: StatusParameter = {
        ...param,
        parameter: getStatusConfigParameterLabel(param.parameter, newUnits),
      };

      if (!paramKey || !isStatusConfigUnitParameter(paramKey)) return updated;

      Object.keys(param).forEach((field) => {
        if (!field.includes('Min') && !field.includes('Max')) return;
        const raw = String(param[field] ?? '');
        const val = parseFloat(raw);
        if (raw === '' || isNaN(val)) return;

        const baseValue = convertStatusConfigToBase(val, paramKey, oldUnits[paramKey]);
        const displayValue = convertStatusConfigFromBase(baseValue, paramKey, newUnits[paramKey]);
        updated[field] = formatDisplayDecimals(displayValue, param.parameter);
      });

      return updated;
    });

  const [parameters, setParameters] = useState<StatusParameter[]>(() => {
    const initialParams = getDynamicParameters();
    return initialParams;
  });
  const [slopeTimeIn, setSlopeTimeIn] = useState('0');
  const [slopeTimeOut, setSlopeTimeOut] = useState('0');
  const [statusCalculationMethod, setStatusCalculationMethod] = useState('Average Zone');
  const [calculationLevel, setCalculationLevel] = useState('Pass Level');
  const [selectedJobNumber, setSelectedJobNumber] = useState<string>('');
  const [requestManualAddJob, setRequestManualAddJob] = useState(false);
  /** Unsaved manual jobs (not yet in get-limits) — keep them visible in the Job Number dropdown. */
  const [pendingManualJobNumbers, setPendingManualJobNumbers] = useState<string[]>([]);
  const [manualSaveTrigger, setManualSaveTrigger] = useState(false);
  const [manualHasModifications, setManualHasModifications] = useState(false);
  const [manualSlopeTimeIn, setManualSlopeTimeIn] = useState('0');
  const [manualSlopeTimeOut, setManualSlopeTimeOut] = useState('0');
  const [manualMinimumWeldTime, setManualMinimumWeldTime] = useState('');
  const manualConfigurationRef = useRef<ManualStatusConfigurationHandle>(null);
  const combinedJobNumbersRef = React.useRef<ReturnType<typeof buildAllStatusJobOptionsFromLimits>>([]);

  // State to track if we've already loaded data for a job number
  const [loadedJobs, setLoadedJobs] = useState<Set<string>>(new Set());

  // Local loading state for save button - always start as false
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Track if save button has been clicked to disable it
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  // Track when we're waiting for data reload after save
  const [waitingForReload, setWaitingForReload] = useState(false);

  // Force reset saving state immediately on component mount
  React.useEffect(() => {
    setIsSaving(false);
    setSaveButtonClicked(false); // Reset button click state on mount
  }, []);

  // State to store parameter values for each job to prevent resetting when switching
  const [jobParameterStates, setJobParameterStates] = useState<Record<string, any>>({});

  // Function to reset loaded jobs (useful when data is refreshed)
  const resetLoadedJobs = () => {
    setLoadedJobs(new Set());
  };

  // Function to refresh data after saving (clears loaded jobs to force fresh data load)
  const refreshAfterSave = () => {
    resetLoadedJobs();

    // Force a refresh of the current job data
    if (selectedJobNumber) {
      // This will trigger the useEffects to reload data
    }
  };

  // State to store configurations for each job
  const [jobConfigurations, setJobConfigurations] = useState<Record<string, {
    parameters: StatusParameter[];
    slopeTimeIn: string;
    slopeTimeOut: string;
    statusCalculationMethod: string;
    calculationLevel: string;
    lastUpdated: number;
  }>>({});

  const getConfigurationKey = (jobNumberValue: string): string =>
    getStatusJobConfigurationKey(combinedJobNumbersRef.current, jobNumberValue);

  // Function to save current job configuration
  const saveCurrentJobConfiguration = (jobNumber: string) => {
    if (jobNumber) {
      const configKey = getConfigurationKey(jobNumber);

      const currentConfig = {
        parameters: parameters.map(param => ({ ...param })),
        slopeTimeIn,
        slopeTimeOut,
        statusCalculationMethod,
        calculationLevel,
        lastUpdated: Date.now()
      };

      setJobConfigurations(prev => ({
        ...prev,
        [configKey]: currentConfig
      }));

    }
  };

  // Function to restore job configuration
  const restoreJobConfiguration = (jobNumber: string) => {
    const configKey = getConfigurationKey(jobNumber);
    const savedConfig = jobConfigurations[configKey];
  //  console.log('savedConfig', savedConfig);
    if (savedConfig) {
      setParameters(savedConfig.parameters);
      setSlopeTimeIn(savedConfig.slopeTimeIn);
      setSlopeTimeOut(savedConfig.slopeTimeOut);
      setStatusCalculationMethod(savedConfig.statusCalculationMethod);
      setCalculationLevel(savedConfig.calculationLevel);
      return true;
    }
    return false;
  };

  // Redux hooks
  const { limitsData, limitsLoading, loadLimitsData } = useLimitsData();
  const {
    projects,
    projectsLoading,
    loading: weldDataLoading
  } = useWeldData();
  const { projectId, projectName, isProjectReady } = useProjectManager();
  const { updateStatusConfiguration, loading: updateLoading, reset: resetUpdateStatus } = useUpdateStatusConfiguration();
  const projectIdNumber = project?.project?.projectId
    ? Number(project.project.projectId)
    : undefined;
  const manualWeldingMachines = useMemo(
    () => getManualStatusWeldingMachineOptions(project?.project?.projectTypes),
    [project?.project?.projectTypes]
  );
  const parameterUnitsRef = React.useRef(parameterUnits);
  parameterUnitsRef.current = parameterUnits;
  const unitsFetchKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    const loadProjectUnits = async () => {
      const id = projectId || project?.project?.projectId;
      if (!id) return;

      const fetchKey = `${id}-${unitsRefreshToken}`;
      if (unitsFetchKeyRef.current === fetchKey) return;
      unitsFetchKeyRef.current = fetchKey;

      const oldUnits = parameterUnitsRef.current;

      try {
        const response = await getReportColumns(Number(id));
        const newUnits = extractStatusConfigUnits(response.data.project.units, projectDefaultUnit);
        const unitsChanged = JSON.stringify(oldUnits) !== JSON.stringify(newUnits);

        if (unitsRefreshToken > 0 && unitsChanged) {
          setParameters((prev) => applyUnitsToParameters(prev, oldUnits, newUnits));
        } else if (unitsChanged) {
          setParameters((prev) =>
            prev.map((param) => ({
              ...param,
              parameter: getStatusConfigParameterLabel(param.parameter, newUnits),
            }))
          );
        }

        if (unitsChanged) {
          setParameterUnits(newUnits);
          if (unitsRefreshToken === 0) {
            resetLoadedJobs();
          }
        }
      } catch {
        const fallbackUnits = getDefaultStatusConfigUnits(projectDefaultUnit);
        if (JSON.stringify(parameterUnitsRef.current) !== JSON.stringify(fallbackUnits)) {
          setParameterUnits(fallbackUnits);
          setParameters((prev) =>
            prev.map((param) => ({
              ...param,
              parameter: getStatusConfigParameterLabel(param.parameter, fallbackUnits),
            }))
          );
        }
      }
    };

    loadProjectUnits();
  }, [projectId, project?.project?.projectId, unitsRefreshToken, projectDefaultUnit]);

  // // Reset update status when component unmounts
  // useEffect(() => {
  //   return () => {
  //     resetLoadedJobs();
  //     resetUpdateStatus();
  //   };
  // }, [resetUpdateStatus]);

  // // Reset loading states when component mounts or modal opens
  // useEffect(() => {
  //   // Force reset local saving state first
  //   setIsSaving(false);
  //   setSaveButtonClicked(false); // Reset button click state
  //   // Reset all loading states when component mounts
  //   resetUpdateStatus();
  //   // Reset editing state to false when component mounts
  //   if (externalSetIsEditing) {
  //     externalSetIsEditing(false);
  //   }
  //   // Reset validation state
  //   setValidationErrors([]);
  //   setShowValidationModal(false);

  //   // Add multiple timeout-based resets as fallback for Redux state
  //   const timeoutId1 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 100);

  //   const timeoutId2 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 500);

  //   const timeoutId3 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 1000);

  //   return () => {
  //     clearTimeout(timeoutId1);
  //     clearTimeout(timeoutId2);
  //     clearTimeout(timeoutId3);
  //   };
  // }, [resetUpdateStatus, externalSetIsEditing]);

  // // Reset loading states when job number changes
  // useEffect(() => {
  //   // Reset loading states when switching jobs
  //   setIsSaving(false);
  //   setSaveButtonClicked(false); // Reset button click state when switching jobs
  //   resetUpdateStatus();
  // }, [selectedJobNumber, resetUpdateStatus]);

  // // Simple timeout-based reset to ensure button is always re-enabled
  // useEffect(() => {
  //   if (isSaving) {
  //     // Force reset after 30 seconds maximum to prevent stuck states
  //     const timeoutId = setTimeout(() => {
  //       setIsSaving(false);
  //       setSaveButtonClicked(false);
  //     }, 30000);

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [isSaving]);

  // Simple loading state - show loader only when actively saving
  const isActuallyLoading = isSaving;

  const combinedJobNumbers = useMemo(
    () => buildAllStatusJobOptionsFromLimits(limitsData),
    [limitsData]
  );
  combinedJobNumbersRef.current = combinedJobNumbers;

  // Drop pending entries once they appear in API limits (after save + reload).
  useEffect(() => {
    setPendingManualJobNumbers((prev) =>
      prev.filter((job) => !resolveStatusJobOption(combinedJobNumbers, job))
    );
  }, [combinedJobNumbers]);

  const jobSelectOptions = useMemo(() => {
    const apiOptions = combinedJobNumbers.map((job) => ({
      id: job.value,
      name: job.value,
    }));
    const pendingOptions = pendingManualJobNumbers
      .filter((job) => !resolveStatusJobOption(combinedJobNumbers, job))
      .map((job) => ({ id: `pending-${job}`, name: job }));
    // Also include current selection if it is a new unsaved manual job not yet tracked.
    if (
      selectedJobNumber &&
      isManualStatusJob('', selectedJobNumber) &&
      !resolveStatusJobOption(combinedJobNumbers, selectedJobNumber) &&
      !pendingOptions.some((opt) => opt.name === selectedJobNumber)
    ) {
      pendingOptions.unshift({ id: `pending-${selectedJobNumber}`, name: selectedJobNumber });
    }
    return [...pendingOptions, ...apiOptions];
  }, [combinedJobNumbers, pendingManualJobNumbers, selectedJobNumber]);

  const jobNumbers = useMemo(
    () => combinedJobNumbers.filter((job) => !job.isManual),
    [combinedJobNumbers]
  );

  const selectedJobOption = useMemo(
    () => resolveStatusJobOption(combinedJobNumbers, selectedJobNumber),
    [combinedJobNumbers, selectedJobNumber]
  );

  const isManualJobSelected = useMemo(
    () =>
      Boolean(
        selectedJobOption?.isManual ||
          isManualStatusJob(selectedJobOption?.label ?? '', selectedJobNumber)
      ),
    [selectedJobNumber, selectedJobOption]
  );

  // Watch for external save trigger
  useEffect(() => {
    if (triggerSave && isEditing) {
      if (isManualJobSelected) {
        setManualSaveTrigger(true);
        setTimeout(() => setManualSaveTrigger(false), 100);
      } else {
        handleSave();
      }
    }
  }, [triggerSave, isEditing, isManualJobSelected]);

  // Notify parent when validation errors change
  useEffect(() => {
    if (isManualJobSelected) return;
    if (onValidationErrorsChange) {
      onValidationErrorsChange(validationErrors.length > 0, validationErrors);
    }
  }, [validationErrors, isManualJobSelected, onValidationErrorsChange]);

  useEffect(() => {
    if (isModalOpen) {
      setIsDirty(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    onDirtyChange?.(isManualJobSelected ? manualHasModifications : isDirty);
  }, [isDirty, isManualJobSelected, manualHasModifications, onDirtyChange]);

  useEffect(() => {
    if (!onEnabledParameterKeysChange || isManualJobSelected) return;
    const enabled: Record<string, boolean> = {};
    parameters.forEach((param) => {
      const key = getParameterApiKey(param.parameter);
      if (key) enabled[key] = param.enabled;
    });
    onEnabledParameterKeysChange(enabled);
  }, [parameters, isManualJobSelected, onEnabledParameterKeysChange]);

  useEffect(() => {
    if (!isModalOpen) {
      setRequestManualAddJob(false);
      setManualSaveTrigger(false);
      setManualHasModifications(false);
      return;
    }

    if (project?.project?.projectNumber && projects && projects.length > 0) {
      loadLimitsData();
    } else if (project?.project?.projectId) {
      loadLimitsData();
    }
  }, [
    isModalOpen,
    loadLimitsData,
    project?.project?.projectId,
    project?.project?.projectNumber,
    projects?.length,
  ]);

  const handleManualSettingsSync = useCallback(
    (settings: { slopeTimeIn: string; slopeTimeOut: string; minimumWeldTime: string }) => {
      setManualSlopeTimeIn(settings.slopeTimeIn);
      setManualSlopeTimeOut(settings.slopeTimeOut);
      setManualMinimumWeldTime(settings.minimumWeldTime);
    },
    []
  );

  const manualControlledSettings = useMemo(
    () => ({
      slopeTimeIn: manualSlopeTimeIn,
      slopeTimeOut: manualSlopeTimeOut,
      minimumWeldTime: manualMinimumWeldTime,
      onSlopeTimeInChange: (value: string) => {
        setManualSlopeTimeIn(value);
        manualConfigurationRef.current?.markDirty();
      },
      onSlopeTimeOutChange: (value: string) => {
        setManualSlopeTimeOut(value);
        manualConfigurationRef.current?.markDirty();
      },
      onMinimumWeldTimeChange: (value: string) => {
        setManualMinimumWeldTime(value);
        manualConfigurationRef.current?.markDirty();
      },
    }),
    [manualMinimumWeldTime, manualSlopeTimeIn, manualSlopeTimeOut]
  );

  // Resolve job on the same render as data so we don't flash the empty state
  // before useEffect/layoutEffect can call setSelectedJobNumber.
  const resolvedJobNumber = React.useMemo(() => {
    if (selectedJobNumber) {
      // Keep manual / pending selections even when they are not in automated jobNumbers.
      if (
        selectedJobOption?.isManual ||
        isManualStatusJob(selectedJobOption?.label ?? '', selectedJobNumber) ||
        pendingManualJobNumbers.includes(selectedJobNumber)
      ) {
        return selectedJobNumber;
      }
      if (jobNumbers.some((job) => job.value === selectedJobNumber)) {
        return selectedJobNumber;
      }
      if (combinedJobNumbers.some((job) => job.value === selectedJobNumber)) {
        return selectedJobNumber;
      }
    }
    return jobNumbers[0]?.value ?? combinedJobNumbers[0]?.value ?? '';
  }, [
    selectedJobNumber,
    selectedJobOption,
    jobNumbers,
    combinedJobNumbers,
    pendingManualJobNumbers,
  ]);

  const resolvedJobOption = useMemo(
    () => resolveStatusJobOption(combinedJobNumbers, resolvedJobNumber),
    [combinedJobNumbers, resolvedJobNumber]
  );

  // Filter automated limits rows for the resolved job; manual jobs use ManualStatusConfiguration.
  const filteredData = React.useMemo(() => {
    if (!limitsData?.allDataSet || !resolvedJobOption || resolvedJobOption.isManual) return [];

    const jobNum = resolvedJobOption.jobNumber;
    const pipeName = resolvedJobOption.pipeName;


    return limitsData.allDataSet.filter((item: LimitsDataItem) => {
      const matchesJobNumber = item.job_number === jobNum;
      const matchesPipeName = pipeName
        ? item.pipe_name === pipeName
        : (item.pipe_name == null || item.pipe_name === "null" || item.pipe_name === "");

      return matchesJobNumber && matchesPipeName;
    });
  }, [limitsData, resolvedJobOption]);


  // // Group data by pass group name
  // const groupedData = React.useMemo(() => {
  //   if (!filteredData.length) return {};

  //   const grouped: Record<string, LimitsDataItem[]> = {};
  //   filteredData.forEach((item: LimitsDataItem) => {
  //     const passGroup = item.pass_group_name || 'Unknown';
  //     if (!grouped[passGroup]) {
  //       grouped[passGroup] = [];
  //     }
  //     grouped[passGroup].push(item);
  //   });

  //   return grouped;
  // }, [filteredData]);

  // Get unique pass groups for the selected job number
  const uniquePassGroups = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const passGroups = new Set<string>();
    filteredData.forEach((item: LimitsDataItem) => {
      if (item.pass_group_name) {
        passGroups.add(item.pass_group_name);
      }
    });

    // Display order only — same pass names; save/validation still use original names
    return sortPassGroupsForStatusConfig(passGroups);
  }, [filteredData]);

  // // Wait for data reload to complete after save, then update UI
  // useEffect(() => {
  //   if (waitingForReload && !limitsLoading && limitsData) {
  //   console.log('waitingForReload', waitingForReload);
  //     // Data has finished loading, clear loaded jobs to force UI refresh
  //     // This will trigger the slope time useEffect to reload from API
  //     resetLoadedJobs();
  //     setWaitingForReload(false);

  //     // Force update slope values from fresh API data for current job
  //     if (selectedJobNumber && limitsData?.weldDetails) {
  //       const jobNum = selectedJobNumber.split(':')[0];
  //       const weldDetail = limitsData.weldDetails.find(
  //         (detail: WeldDetail) => detail.job_number === jobNum
  //       );

  //       if (weldDetail) {
  //         setSlopeTimeIn(weldDetail.slope_in_time?.toString() || '0');
  //         setSlopeTimeOut(weldDetail.slope_out_time?.toString() || '0');
  //       }
  //     }

  //     // Call onSaveComplete callback to notify parent that API call is done
  //     if (onSaveComplete) {
  //       onSaveComplete();
  //     }
  //   }
  // }, [waitingForReload, limitsLoading, limitsData, resetLoadedJobs, onSaveComplete, selectedJobNumber]);

  // Keep selectedJobNumber in sync before paint so dependent effects see a valid job
  React.useLayoutEffect(() => {
    if (resolvedJobNumber && resolvedJobNumber !== selectedJobNumber) {
      setSelectedJobNumber(resolvedJobNumber);
    }
  }, [resolvedJobNumber, selectedJobNumber]);


  // Clear validation errors when no job is selected
  useEffect(() => {
    if (!resolvedJobNumber || filteredData.length === 0) {
      if (validationErrors.length > 0) {
        setValidationErrors([]);
        setShowValidationModal(false);
      }
    }
  }, [resolvedJobNumber, filteredData.length, validationErrors.length]);

  // Notify parent component when job or pipe information changes
  useEffect(() => {
    //console.log('selectedJobNumber', selectedJobNumber);
    if (resolvedJobNumber && filteredData.length > 0 && onJobPipeInfoChange) {
      const pipeName = filteredData[0]?.pipe_name || 'Unknown';
      onJobPipeInfoChange(resolvedJobNumber, pipeName);
    }
  }, [resolvedJobNumber, filteredData, onJobPipeInfoChange]);

  // Update parameters when filtered data changes
  useEffect(() => {
    if (filteredData.length > 0) {
      // If job is already loaded, skip this update to prevent infinite loop
      if (selectedJobNumber && loadedJobs.has(selectedJobNumber)) {
        return;
      }

      // Check if we have a saved state for this job using configuration key
      const configKey = selectedJobNumber ? getConfigurationKey(selectedJobNumber) : '';
      const hasSavedState = configKey && jobParameterStates[configKey];

      if (hasSavedState) {
        // Restore the saved state instead of loading from API
        restoreJobState(selectedJobNumber);
        // Mark as loaded to prevent future API updates
        setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
        updateLoadedJobsState(selectedJobNumber);
        return;
      }

      // First time loading - create fresh parameters
      const updatedParameters = getDynamicParameters().map(param => {
        const newParam = { ...param };

        // Group data by pass_group_name
        const passGroups = filteredData.reduce((acc: Record<string, LimitsDataItem[]>, item: LimitsDataItem) => {
          if (!acc[item.pass_group_name]) {
            acc[item.pass_group_name] = [];
          }
          acc[item.pass_group_name].push(item);
          return acc;
        }, {} as Record<string, LimitsDataItem[]>);

        // Update parameters based on pass groups
        Object.entries(passGroups).forEach(([passGroup, items]) => {
          const typedItems = items as LimitsDataItem[];
          if (typedItems.length > 0) {
            // Dynamically set values based on pass group
            const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
            const minKey = `${passGroupKey}Min`;
            const maxKey = `${passGroupKey}Max`;

            // Use parameter name matching to determine which values to set
            if (param.parameter.includes('Current')) {
              const currentMins = typedItems.map((item: LimitsDataItem) => item.current_min).filter(Boolean);
              const currentMaxs = typedItems.map((item: LimitsDataItem) => item.current_max).filter(Boolean);
              const minValue = currentMins.length > 0 ? Math.min(...currentMins) : 0;
              const maxValue = currentMaxs.length > 0 ? Math.max(...currentMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Volts')) {
              const voltsMins = typedItems.map((item: LimitsDataItem) => item.volts_min).filter(Boolean);
              const voltsMaxs = typedItems.map((item: LimitsDataItem) => item.volts_max).filter(Boolean);
              const minValue = voltsMins.length > 0 ? Math.min(...voltsMins) : 0;
              const maxValue = voltsMaxs.length > 0 ? Math.max(...voltsMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Wire Speed')) {
              const wireSpeedMins = typedItems.map((item: LimitsDataItem) => item.wire_speed_min).filter(Boolean);
              const wireSpeedMaxs = typedItems.map((item: LimitsDataItem) => item.wire_speed_max).filter(Boolean);
              const minValue = wireSpeedMins.length > 0 ? Math.min(...wireSpeedMins) : 0;
              const maxValue = wireSpeedMaxs.length > 0 ? Math.max(...wireSpeedMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Travel Speed')) {
              const travelSpeedMins = typedItems.map((item: LimitsDataItem) => item.travel_speed_min).filter(Boolean);
              const travelSpeedMaxs = typedItems.map((item: LimitsDataItem) => item.travel_speed_max).filter(Boolean);
              const minValue = travelSpeedMins.length > 0 ? Math.min(...travelSpeedMins) : 0;
              const maxValue = travelSpeedMaxs.length > 0 ? Math.max(...travelSpeedMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Oscillation Width')) {
              const oscillationMins = typedItems.map((item: LimitsDataItem) => item.oscillation_width_min).filter(Boolean);
              const oscillationMaxs = typedItems.map((item: LimitsDataItem) => item.oscillation_width_max).filter(Boolean);
              const minValue = oscillationMins.length > 0 ? Math.min(...oscillationMins) : 0;
              const maxValue = oscillationMaxs.length > 0 ? Math.max(...oscillationMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Heat')) {
              const heatMins = typedItems.map((item: LimitsDataItem) => item.heat_min).filter(val => val !== null) as number[];
              const heatMaxs = typedItems.map((item: LimitsDataItem) => item.heat_max).filter(val => val !== null) as number[];
              const minValue = heatMins.length > 0 ? Math.min(...heatMins) : 0;
              const maxValue = heatMaxs.length > 0 ? Math.max(...heatMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('True Energy')) {
              const trueEnergyMins = typedItems.map((item: LimitsDataItem) => item.true_energy_min).filter(val => val !== null) as number[];
              const trueEnergyMaxs = typedItems.map((item: LimitsDataItem) => item.true_energy_max).filter(val => val !== null) as number[];
              const minValue = trueEnergyMins.length > 0 ? Math.min(...trueEnergyMins) : 0;
              const maxValue = trueEnergyMaxs.length > 0 ? Math.max(...trueEnergyMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            }
          }
        });

        return newParam;
      });

      setParameters(updatedParameters);

      // Mark this job as loaded after successfully updating parameters
      if (selectedJobNumber) {
        setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
        updateLoadedJobsState(selectedJobNumber);
      }
    }
  }, [filteredData, selectedJobNumber, loadedJobs, jobParameterStates]);

  // Update parameter checkbox states when limits data is loaded and job number is selected
  useEffect(() => {
    if (isManualJobSelected || !limitsData?.weldDetails || !selectedJobOption || selectedJobOption.isManual) {
      return;
    }

    const hasRestoredConfig = restoreJobConfiguration(selectedJobNumber);
    if (hasRestoredConfig) {
      return;
    }

    const jobNum = selectedJobOption.jobNumber;
    const pipeNameFromValue = selectedJobOption.pipeName;
    const weldingSystem = selectedJobOption.label.split(':')[0]?.trim().toLowerCase() || '';
    const isM500 = weldingSystem === 'm500';

    let jobWeldDetails: any;

    if (isM500 && pipeNameFromValue) {
      jobWeldDetails = limitsData.weldDetails.find((item: any) => {
        if (item.job_number === jobNum) {
          return item.pipe_name ? item.pipe_name === pipeNameFromValue : true;
        }
        return false;
      });

      if (!jobWeldDetails) {
        jobWeldDetails = limitsData.weldDetails.find((item: any) => item.job_number === jobNum);
      }
    } else {
      jobWeldDetails = limitsData.weldDetails.find((item: any) => item.job_number === jobNum);
    }

    if (jobWeldDetails?.selected_parameter_list) {
      try {
        const apiSelectedParams = typeof jobWeldDetails.selected_parameter_list === 'string'
          ? JSON.parse(jobWeldDetails.selected_parameter_list)
          : jobWeldDetails.selected_parameter_list;

        setParameters(prev => prev.map(param => {
          const newParam = { ...param };
          const apiKey = getParameterApiKey(param.parameter);

          if (apiKey && apiSelectedParams.hasOwnProperty(apiKey)) {
            newParam.enabled = apiSelectedParams[apiKey] || false;
          }

          return newParam;
        }));

        setSlopeTimeIn(jobWeldDetails.slope_in_time?.toString() || '0');
        setSlopeTimeOut(jobWeldDetails.slope_out_time?.toString() || '0');
      } catch (error) {
        console.error('Error parsing selected_parameter_list:', error);
      }
    }
  }, [
    isManualJobSelected,
    limitsData?.weldDetails,
    selectedJobNumber,
    selectedJobOption,
    jobConfigurations,
  ]);

  // Update slope time and status calculation method when job number changes
  useEffect(() => {
    if (
      isManualJobSelected ||
      !selectedJobNumber ||
      !selectedJobOption ||
      selectedJobOption.isManual ||
      !limitsData?.weldDetails
    ) {
      return;
    }

    const jobNum = selectedJobOption.jobNumber;
    const pipeNameFromValue = selectedJobOption.pipeName;
    const configKey = getConfigurationKey(selectedJobNumber);
    const savedConfig = jobConfigurations[configKey];

    if (savedConfig) {
      setSlopeTimeIn(savedConfig.slopeTimeIn);
      setSlopeTimeOut(savedConfig.slopeTimeOut);
      setStatusCalculationMethod(savedConfig.statusCalculationMethod);
      setCalculationLevel(savedConfig.calculationLevel);
      setParameters(savedConfig.parameters);
      return;
    }

    if (loadedJobs.has(selectedJobNumber)) {
      return;
    }

    const weldingSystem = selectedJobOption.label.split(':')[0]?.trim().toLowerCase() || '';
    let weldDetail: WeldDetail | undefined;

    if (weldingSystem === 'm500' && pipeNameFromValue) {
      weldDetail = limitsData.weldDetails.find((detail: any) => {
        if (detail.job_number === jobNum) {
          return detail.pipe_name ? detail.pipe_name === pipeNameFromValue : true;
        }
        return false;
      });

      if (!weldDetail) {
        weldDetail = limitsData.weldDetails.find(
          (detail: WeldDetail) => detail.job_number === jobNum
        );
      }
    } else {
      weldDetail = limitsData.weldDetails.find(
        (detail: WeldDetail) => detail.job_number === jobNum
      );
    }

    if (weldDetail) {
      setSlopeTimeIn(weldDetail.slope_in_time?.toString() || '0');
      setSlopeTimeOut(weldDetail.slope_out_time?.toString() || '0');
      setStatusCalculationMethod(limitsData?.projectData?.statusCalculationMethod || 'Pass Level');
      setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
      updateLoadedJobsState(selectedJobNumber);
    }
  }, [
    isManualJobSelected,
    selectedJobNumber,
    selectedJobOption,
    limitsData,
    loadedJobs,
    jobConfigurations,
  ]);

  // Initialize status calculation method from project data when component mounts
  useEffect(() => {
    if (limitsData?.projectData?.statusCalculationMethod) {
      setStatusCalculationMethod(limitsData.projectData.statusCalculationMethod);
    }
  }, [limitsData?.projectData?.statusCalculationMethod]);

  // Console log the current configuration values
  useEffect(() => {
    // Configuration values updated - no logging needed
  }, [slopeTimeIn, slopeTimeOut, statusCalculationMethod, calculationLevel, selectedJobNumber, limitsData?.projectData?.statusCalculationMethod]);

  // // Console log when job number changes
  // useEffect(() => {
  //   if (selectedJobNumber) {
  //     // Job number changed - no logging needed
  //   }
  // }, [selectedJobNumber, uniquePassGroups]);

  // // Console log when limits data is loaded
  // useEffect(() => {
  //   // Limits data loaded - no logging needed
  // }, [limitsData]);

  // // Console log when parameters are updated
  // useEffect(() => {
  //   // Parameters updated - no logging needed
  // }, []);

  // Function to save current job's parameter state before switching
  const saveCurrentJobState = () => {
    if (selectedJobNumber && parameters.length > 0) {
      const configKey = getConfigurationKey(selectedJobNumber);

      const currentState = parameters.reduce((acc, param) => {
        // Extract all pass group values for this parameter
        const passGroupValues: any = {};
        Object.keys(param).forEach(key => {
          if (key.includes('Min') || key.includes('Max')) {
            passGroupValues[key] = param[key];
          }
        });

        acc[param.parameter] = {
          enabled: param.enabled,
          ...passGroupValues
        };
        return acc;
      }, {} as any);

      setJobParameterStates(prev => ({
        ...prev,
        [configKey]: currentState
      }));

      // Also save slope values and configuration to jobConfigurations
      saveCurrentJobConfiguration(selectedJobNumber);
    }
  };

  // Function to restore job's parameter state
  const restoreJobState = (jobNumber: string) => {
    const configKey = getConfigurationKey(jobNumber);
    const savedState = jobParameterStates[configKey];
    if (savedState && parameters.length > 0) {
      const restoredParameters = parameters.map(param => {
        const newParam = { ...param };
        const savedParamState = savedState[param.parameter];

        if (savedParamState) {
          newParam.enabled = savedParamState.enabled;
          // Restore all pass group values
          Object.keys(savedParamState).forEach(key => {
            if (key.includes('Min') || key.includes('Max')) {
              newParam[key] = savedParamState[key];
            }
          });
        }

        return newParam;
      });

      setParameters(restoredParameters);
    }
  };

  const handleManualSaveComplete = () => {
    onSaveComplete?.();
  };

  const handleManualJobAdded = (displayJobNumber: string) => {
    if (!displayJobNumber) return;
    setPendingManualJobNumbers((prev) =>
      prev.includes(displayJobNumber) ? prev : [...prev, displayJobNumber]
    );
    setSelectedJobNumber(displayJobNumber);
  };

  // Handle job number change with state persistence
  const handleJobNumberChange = (newJobNumber: string) => {
    // Clear validation errors when job number changes
    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }

    // Save current job's state and configuration before switching (synchronously)
    let nextConfigurations = jobConfigurations;
    if (selectedJobNumber) {
      const configKey = getConfigurationKey(selectedJobNumber);
      const currentConfig = {
        parameters: parameters.map(param => ({ ...param })),
        slopeTimeIn,
        slopeTimeOut,
        statusCalculationMethod,
        calculationLevel,
        lastUpdated: Date.now()
      };
      nextConfigurations = {
        ...jobConfigurations,
        [configKey]: currentConfig
      };
      setJobConfigurations(nextConfigurations);
      saveCurrentJobState();
    }

    // Update selected job number (display value, e.g. `p600z: IWM`)
    setSelectedJobNumber(newJobNumber);

    // Restore the new job's configuration (includes slope values) if it exists
    const newConfigKey = getConfigurationKey(newJobNumber);
    const savedConfig = nextConfigurations[newConfigKey];
    if (savedConfig) {
      setParameters(savedConfig.parameters);
      setSlopeTimeIn(savedConfig.slopeTimeIn);
      setSlopeTimeOut(savedConfig.slopeTimeOut);
      setStatusCalculationMethod(savedConfig.statusCalculationMethod);
      setCalculationLevel(savedConfig.calculationLevel);
      return;
    }

    // If no saved configuration, restore parameter state if it exists
    if (jobParameterStates[newConfigKey]) {
      restoreJobState(newJobNumber);
    }
  };

  const handleParameterChange = (id: string, field: keyof StatusParameter, value: string | boolean) => {
    setIsDirty(true);

    // Clear validation errors when parameters change
    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }

    setParameters(prev => {
      const updated = prev.map(param => {
        if (param.id === id) {
          // If this is a value field (min/max), store the user input as-is
          if (typeof value === 'string' && (field.toString().includes('Min') || field.toString().includes('Max'))) {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue)) {
              // Store the user input directly without conversion
              // Conversion will happen only when saving to API
              // This ensures user sees exactly what they typed
              return { ...param, [field]: value };
            }
          }
          // For non-value fields (like enabled), just update normally
          return { ...param, [field]: value };
        }
        return param;
      });


      return updated;
    });
  };

  // Wrapper functions to clear validation errors when configuration changes
  const handleSlopeTimeInChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setSlopeTimeIn(value);
  };

  const handleSlopeTimeOutChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setSlopeTimeOut(value);
  };

  const handleStatusCalculationMethodChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setStatusCalculationMethod(value);
  };

  const handleCalculationLevelChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setCalculationLevel(value);
  };

  // Function to check if a specific input field has validation errors
  const hasInputError = (parameterName: string, passGroup: string, fieldType: 'min' | 'max'): boolean => {
    return validationErrors.some(error =>
      error.parameter === parameterName &&
      error.passGroup === passGroup &&
      (fieldType === 'min' ? error.minValue : error.maxValue) !== undefined
    );
  };

  // Function to validate parameters before saving
  const validateParameters = (
    paramsToValidate: StatusParameter[] = parameters,
    slopeInToValidate: string = slopeTimeIn,
    slopeOutToValidate: string = slopeTimeOut,
    passGroupsToValidate: string[] = uniquePassGroups
  ): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Validate slope time values (0-10 range, can be negative)
    const slopeInValue = parseFloat(slopeInToValidate);
    const slopeOutValue = parseFloat(slopeOutToValidate);

    // Check if slope values are within valid range (-10 to 10)
    if (slopeInValue < -10 || slopeInValue > 10) {
      errors.push({
        parameter: 'Slope In Time',
        minValue: slopeInValue,
        maxValue: slopeInValue,
        unit: 'sec',
        passGroup: 'Slope',
        type: 'out_of_range'
      });
    }

    if (slopeOutValue < -10 || slopeOutValue > 10) {
      errors.push({
        parameter: 'Slope Out Time',
        minValue: slopeOutValue,
        maxValue: slopeOutValue,
        unit: 'sec',
        passGroup: 'Slope',
        type: 'out_of_range'
      });
    }

    paramsToValidate.forEach(param => {
      // Only validate if the parameter is enabled (checkbox is checked)
      if (!param.enabled) {
        return; // Skip validation for disabled parameters
      }

      passGroupsToValidate.forEach(passGroup => {
        const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
        const minKey = `${passGroupKey}Min`;
        const maxKey = `${passGroupKey}Max`;

        const minValue = parseFloat(String(param[minKey] || '0'));
        const maxValue = parseFloat(String(param[maxKey] || '0'));

        // Skip validation if both min and max values are 0 (treat as no limits set)
        if (minValue === 0 && maxValue === 0) {
          return; // Skip all validation for this parameter
        }

        // Check if min value is greater than or equal to max value
        if (minValue >= maxValue) {
          const unit = getStatusConfigParameterUnit(param.parameter, parameterUnits);

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'min_max_mismatch'
          });
        }

        // Additional validation: check for negative values
        if (minValue < 0) {
          const unit = getStatusConfigParameterUnit(param.parameter, parameterUnits);

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'negative_value'
          });
        }

        // Additional validation: check for max value being negative
        if (maxValue < 0) {
          const unit = getStatusConfigParameterUnit(param.parameter, parameterUnits);

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'negative_value'
          });
        }
      });
    });


    return errors;
  };

  const handleSave = async () => {
    try {
      // Set local loading state
      setIsSaving(true);

      // Snapshot current job into the per-job cache synchronously so Save can include
      // every job the user edited before switching away.
      const currentConfigSnapshot = {
        parameters: parameters.map(param => ({ ...param })),
        slopeTimeIn,
        slopeTimeOut,
        statusCalculationMethod,
        calculationLevel,
        lastUpdated: Date.now()
      };
      const activeConfigKey = selectedJobNumber ? getConfigurationKey(selectedJobNumber) : '';
      const allJobConfigurations = selectedJobNumber
        ? {
            ...jobConfigurations,
            [activeConfigKey]: currentConfigSnapshot
          }
        : { ...jobConfigurations };
      setJobConfigurations(allJobConfigurations);
      if (selectedJobNumber) {
        saveCurrentJobState();
      }

      // Reset any previous update status
      resetUpdateStatus();

      const jobsToSave = selectStatusJobOptionsToSave(
        jobNumbers,
        selectedJobNumber,
        Object.keys(allJobConfigurations),
        getConfigurationKey
      );

      const resolveJobPassGroups = (jobOption: (typeof jobNumbers)[number]): string[] => {
        const jobNumber = jobOption.jobNumber;
        const pipeName = jobOption.pipeName;
        const weldingSystem = jobOption.label.split(':')[0]?.trim().toLowerCase() || '';
        const isM500 = weldingSystem === 'm500';

        return (
          limitsData?.allDataSet
            ?.filter((item: LimitsDataItem) => {
              if (isM500 && pipeName) {
                return item.job_number === jobNumber && item.pipe_name === pipeName;
              }
              return (
                item.job_number === jobNumber &&
                (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null')
              );
            })
            ?.map((item: LimitsDataItem) => item.pass_group_name)
            ?.filter((value: string | undefined): value is string => Boolean(value))
            ?.filter((value: string, index: number, self: string[]) => self.indexOf(value) === index) ||
          []
        );
      };

      // Validate every job included in this save
      const errors = jobsToSave.flatMap((jobOption) => {
        const configKey = getConfigurationKey(jobOption.value);
        const jobConfig = allJobConfigurations[configKey] ?? currentConfigSnapshot;
        return validateParameters(
          jobConfig.parameters,
          jobConfig.slopeTimeIn,
          jobConfig.slopeTimeOut,
          resolveJobPassGroups(jobOption)
        );
      });
      if (errors.length > 0) {
        setValidationErrors(errors);
        setShowValidationModal(true);
        // Reset saving states when validation fails
        setIsSaving(false);
        setSaveButtonClicked(false);
        // Notify parent that save was attempted but validation failed
        if (onValidationFailure) {
          onValidationFailure();
        }
        return;
      }

      //console.log('jobNumbers', jobNumbers);
      //console.log('selectedJobNumber', selectedJobNumber);

      // Format data according to API structure - include every edited/cached job
      const payload = jobsToSave.map(jobOption => {
        const jobNumber = jobOption.jobNumber;
        const pipeName = jobOption.pipeName;
        const jobConfigKey = getConfigurationKey(jobOption.value);
        const jobConfig = allJobConfigurations[jobConfigKey] ?? currentConfigSnapshot;
        const jobParameters = jobConfig.parameters;
        const jobSlopeTimeIn = jobConfig.slopeTimeIn;
        const jobSlopeTimeOut = jobConfig.slopeTimeOut;
        const jobStatusCalculationMethod = jobConfig.statusCalculationMethod;
        const jobCalculationLevel = jobConfig.calculationLevel;

        // Get welding system from jobOption label (format: "welding_system: jobNum: pipeName")
        const weldingSystem = jobOption.label.split(':')[0]?.trim().toLowerCase() || '';
        const isM500 = weldingSystem === 'm500';

        // For m500: match by job_number and pipe_name
        // For others (p625, etc.): match by job_number only, pipe_name should be null/empty
        const jobData = limitsData?.allDataSet?.find((item: LimitsDataItem) => {
          if (isM500 && pipeName) {
            return item.job_number === jobNumber && item.pipe_name === pipeName;
          } else {
            // For non-m500, pipe_name should be null, empty, or undefined
            return item.job_number === jobNumber &&
              (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null');
          }
        });

        // console.log('jobData', jobData);
        // console.log('pipeName', pipeName);
        // console.log('jobNumber', jobNumber);
        // console.log('weldingSystem', weldingSystem);
        // console.log('isM500', isM500);
        //console.log('limitsData?.allDataSet', limitsData?.allDataSet);

        // Get pass groups specific to THIS job number
        const jobSpecificPassGroups = resolveJobPassGroups(jobOption);



        // Get pass updates for this job - ONLY include pass groups that belong to this job
        const passUpdates = jobSpecificPassGroups.map((passGroup: string) => {

          // Create the pass update object with exact parameter names from the working curl
          const passUpdate: any = {
            pass_group_name: passGroup
          };

          // Get the actual data for this pass group from limits data
          // For m500: filter by job_number, pipe_name, and pass_group_name
          // For others: filter by job_number and pass_group_name, pipe_name should be null/empty
          const passGroupData = limitsData?.allDataSet?.filter((item: LimitsDataItem) => {
            if (isM500 && pipeName) {
              return item.job_number === jobNumber &&
                item.pipe_name === pipeName &&
                item.pass_group_name === passGroup;
            } else {
              // For non-m500, pipe_name should be null, empty, or undefined
              return item.job_number === jobNumber &&
                (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null') &&
                item.pass_group_name === passGroup;
            }
          });

          if (passGroupData && passGroupData.length > 0) {
            // Use the first item's data for this pass group
            const firstItem = passGroupData[0];

            // Map parameters to the exact format from the working curl
            jobParameters.forEach(param => {
              // Convert parameter names to match the working curl format using dynamic mapping
              const paramKey = getParameterKey(param.parameter);

              if (paramKey) {
                // Always use this job's cached component state values
                const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                const minKey = `${passGroupKey}Min`;
                const maxKey = `${passGroupKey}Max`;

                let minValue = param[minKey];
                let maxValue = param[maxKey];


                // If no values in component state, fallback to API data
                if (!minValue && minValue !== '0') {
                  if (paramKey === 'current') {
                    minValue = firstItem.current_min;
                  } else if (paramKey === 'volts') {
                    minValue = firstItem.volts_min;
                  } else if (paramKey === 'wire_speed') {
                    minValue = firstItem.wire_speed_min;
                  } else if (paramKey === 'travel_speed') {
                    minValue = firstItem.travel_speed_min;
                  } else if (paramKey === 'oscillation_width') {
                    minValue = firstItem.oscillation_width_min;
                  } else if (paramKey === 'heat') {
                    minValue = firstItem.heat_min;
                  } else if (paramKey === 'true_energy') {
                    minValue = firstItem.true_energy_min;
                  }
                }

                if (!maxValue && maxValue !== '0') {
                  if (paramKey === 'current') {
                    maxValue = firstItem.current_max;
                  } else if (paramKey === 'volts') {
                    maxValue = firstItem.volts_max;
                  } else if (paramKey === 'wire_speed') {
                    maxValue = firstItem.wire_speed_max;
                  } else if (paramKey === 'travel_speed') {
                    maxValue = firstItem.travel_speed_max;
                  } else if (paramKey === 'oscillation_width') {
                    maxValue = firstItem.oscillation_width_max;
                  } else if (paramKey === 'heat') {
                    maxValue = firstItem.heat_max;
                  } else if (paramKey === 'true_energy') {
                    maxValue = firstItem.true_energy_max;
                  }
                }

                // Ensure we have valid values (not null/undefined)
                if (minValue === null || minValue === undefined) {
                  minValue = '0'; // Default fallback as string
                }
                if (maxValue === null || maxValue === undefined) {
                  maxValue = '0'; // Default fallback as string
                }

                // Convert values to API format consistently
                let finalMinValue, finalMaxValue;

                if (typeof minValue === 'string') {
                  finalMinValue = formatValueForAPI(parseFloat(minValue), param.parameter);
                } else {
                  finalMinValue = minValue;
                }

                if (typeof maxValue === 'string') {
                  finalMaxValue = formatValueForAPI(parseFloat(maxValue), param.parameter);
                } else {
                  finalMaxValue = maxValue;
                }

                passUpdate[`${paramKey}_min`] = finalMinValue;
                passUpdate[`${paramKey}_max`] = finalMaxValue;

              }
            });
          } else {
            // If no data found for this pass group, use component state values with fallbacks
            jobParameters.forEach(param => {
              const paramKey = getParameterKey(param.parameter);

              if (paramKey) {
                const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                const minKey = `${passGroupKey}Min`;
                const maxKey = `${passGroupKey}Max`;

                let minValue = param[minKey];
                let maxValue = param[maxKey];

                // Ensure we have valid values (not null/undefined)
                if (minValue === null || minValue === undefined) {
                  minValue = '0'; // Default fallback as string
                }
                if (maxValue === null || maxValue === undefined) {
                  maxValue = '0'; // Default fallback as string
                }

                // Convert string values to API format, keep others as-is
                let finalMinValue, finalMaxValue;

                if (typeof minValue === 'string') {
                  finalMinValue = formatValueForAPI(parseFloat(minValue), param.parameter);
                } else {
                  finalMinValue = minValue;
                }

                if (typeof maxValue === 'string') {
                  finalMaxValue = formatValueForAPI(parseFloat(maxValue), param.parameter);
                } else {
                  finalMaxValue = maxValue;
                }

                passUpdate[`${paramKey}_min`] = finalMinValue;
                passUpdate[`${paramKey}_max`] = finalMaxValue;

              }
            });
          }

          return passUpdate;
        });

        // Create selected parameter list with exact format from working curl
        const selectedParameterList: any = {};

        jobParameters.forEach(param => {
          const paramKey = getParameterApiKey(param.parameter);
          if (paramKey) {
            selectedParameterList[paramKey] = param.enabled;
          }
        });

        return {
          welding_system: jobData?.welding_system || 'p625',
          pipe_name: jobData?.pipe_name || null,
          job_number: jobNumber,
          slope_in: parseInt(jobSlopeTimeIn) || 0,
          slope_out: parseInt(jobSlopeTimeOut) || 0,
          status_calculation_method: jobStatusCalculationMethod,
          status_calculation_level: jobCalculationLevel,
          selected_parameter_list: selectedParameterList,
          pass_updates: passUpdates
        };
      });

      // console.log('payload', payload);
      // Format payload to match the working curl command exactly
      const formattedPayload = {
        payload: payload
      };

      // Call the API - Use centralized project data
      let finalProjectId: number;

      if (isProjectReady && projectId) {
        finalProjectId = Number(projectId);
      } else if (project?.project?.projectId) {
        // Fallback to original project ID if centralized data not ready
        finalProjectId = Number(project.project.projectId);
      } else {
        throw new Error('Project ID not found');
      }
      //  console.log('finalProjectId', finalProjectId);

      const result = updateStatusConfiguration(finalProjectId, formattedPayload);

      const apiResult = await result;

      const savedJobConfig = payload[0];
      if (savedJobConfig) {
        logStatusConfigurationUpdated({
          projectId: finalProjectId,
          projectName: project?.project?.projectName ?? null,
          pipeSize: selectedPipeSize ?? null,
          pipeName: savedJobConfig.pipe_name ?? null,
          jobNumber: savedJobConfig.job_number,
          weldingSystem: savedJobConfig.welding_system ?? null,
          slopeTimeIn: savedJobConfig.slope_in,
          slopeTimeOut: savedJobConfig.slope_out,
          statusCalculationMethod: savedJobConfig.status_calculation_method,
          calculationLevel: savedJobConfig.status_calculation_level,
          enabledParameters: savedJobConfig.selected_parameter_list ?? {},
          passGroupCount: savedJobConfig.pass_updates?.length ?? 0,
        });
      }

      // Reset editing state
      setIsEditing(false);

      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }

      // Show success message
      Notification.success(
        payload.length > 1
          ? `Status Configuration saved for ${payload.length} job numbers`
          : 'Status Configuration saved successfully!'
      );

      setIsDirty(false);

      // Clear loaded jobs to force fresh data load on next access
      resetLoadedJobs();

      // Set waiting state and reload limits data
      setWaitingForReload(true);

      if (onSaveComplete) {
        onSaveComplete();
      }

    } catch (error) {
      console.error('🔍 SAVE VALUES - Error during save:', error);
      Notification.error(`Error saving status configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Reset waiting state on error
      setWaitingForReload(false);

      // Call onSaveComplete callback even on error to close modal
      if (onSaveComplete) {
        onSaveComplete();
      }
    } finally {
      loadLimitsData(); // No parameters needed - uses centralized project data
      // Ensure loading state is reset regardless of success or failure
      setIsSaving(false);
      setSaveButtonClicked(false);
      resetUpdateStatus();
    }
  };


  // Function to update loaded jobs state when switching between jobs
  const updateLoadedJobsState = (jobNumber: string) => {
    if (jobNumber && !loadedJobs.has(jobNumber)) {
      setLoadedJobs(prev => new Set(Array.from(prev).concat(jobNumber)));
    }
  };

  const handleReset = () => {
    setParameters(getDynamicParameters(parameterUnits));
    setSlopeTimeIn('0');
    setSlopeTimeOut('0');
    setStatusCalculationMethod('Average Zone');
    setCalculationLevel('Pass Level');
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
    // Clear validation errors when starting to edit
    if (!isEditing) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
  };

  // Add custom scrollbar styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .status-config-table::-webkit-scrollbar {
        height: 8px;
      }
      .status-config-table::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 4px;
      }
      .status-config-table::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
      }
      .status-config-table::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);



  const hasLimitsRows =
    Array.isArray(limitsData?.allDataSet) && limitsData.allDataSet.length > 0;
  const awaitingJobResolution =
    hasLimitsRows && jobNumbers.length > 0 && !resolvedJobNumber;

  if (limitsLoading || weldDataLoading || projectsLoading || awaitingJobResolution) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#EB1C37]" />
        <span className="ml-2 text-gray-600">Loading status configuration data...</span>
      </div>
    );
  }

  // Show empty state when no jobs exist at all
  if (combinedJobNumbers.length === 0 && manualWeldingMachines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center">
          <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Status Configuration Data</h3>
          <p className="text-gray-600 mb-4">
            No job numbers are available for this project yet.
          </p>
        </div>
      </div>
    );
  }

  // Show empty state when automated job has no pass data (manual jobs render their own UI)
  if (
    !isManualJobSelected &&
    resolvedJobNumber &&
    (!filteredData.length || uniquePassGroups.length === 0)
  ) {

    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center">
          <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Status Configuration Data</h3>
          <p className="text-gray-600 mb-4">
            {!resolvedJobNumber
              ? "Please select a job number to view status configuration data."
              : "No pass groups found for the selected job number. Please check if the job has any welding data."
            }
          </p>
          {!resolvedJobNumber && combinedJobNumbers.length > 0 && (
            <p className="text-sm text-gray-500">
              Available job numbers: {combinedJobNumbers.map((job) => job.label).join(', ')}

            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Validation Error Modal */}
      <ValidationErrorModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        errors={validationErrors}
        jobNumber={resolvedJobNumber || 'N/A'}
        calculationLevel={calculationLevel}
        onErrorsResolved={(resolvedErrors: ValidationError[]) => {
          // Remove resolved errors from validation errors
          const remainingErrors = validationErrors.filter(error =>
            !resolvedErrors.some((resolved: ValidationError) =>
              resolved.parameter === error.parameter &&
              resolved.passGroup === error.passGroup &&
              resolved.type === error.type
            )
          );
          setValidationErrors(remainingErrors);

          // Close modal if no more errors
          if (remainingErrors.length === 0) {
            setShowValidationModal(false);
          }
        }}
      />

      <div className="space-y-4 w-full max-w-full min-w-0">
        {/* Configuration Card — all selectors in a single row with Save on the right */}
        <Card className="border border-[#E2E5EA] shadow-sm max-w-full overflow-visible">
          <CardContent className="p-4 overflow-visible">
            <h3 className="text-[14px] font-semibold text-blueprint-blue mb-4">Configuration</h3>

            <div
              className={
                isManualJobSelected
                  ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4'
                  : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'
              }
            >
              {/* Job Number */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Job Number</Label>
                {isManualJobSelected || manualWeldingMachines.length > 0 ? (
                  <SearchableSelect
                    value={resolvedJobNumber || selectedJobNumber || undefined}
                    onValueChange={handleJobNumberChange}
                    placeholder="Select job number"
                    searchPlaceholder="Search job numbers..."
                    options={jobSelectOptions}
                    onAddNew={
                      manualWeldingMachines.length > 0
                        ? () => setRequestManualAddJob(true)
                        : undefined
                    }
                    addNewText="Add New Job Number"
                    showAddButton={canEdit && manualWeldingMachines.length > 0}
                    disabled={!canEdit || limitsLoading || weldDataLoading || projectsLoading}
                    loading={limitsLoading}
                    loadingText="Loading job numbers..."
                  />
                ) : (
                  <Select
                    value={resolvedJobNumber || selectedJobNumber}
                    onValueChange={handleJobNumberChange}
                    disabled={!canEdit || limitsLoading || weldDataLoading || projectsLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select job number" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobSelectOptions.map((jobOption) => (
                        <SelectItem key={jobOption.id} value={jobOption.name}>
                          {jobOption.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

              </div>

              {/* Status Calculation Method */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Status Calculation Method</Label>
                {isManualJobSelected ? (
                  <div className="flex h-9 items-center rounded-md border border-[#E2E5EA] bg-page-bg px-3 text-[12px] font-medium text-blueprint-blue">
                    Average Pass
                  </div>
                ) : (
                <Select
                  value={statusCalculationMethod}
                  onValueChange={handleStatusCalculationMethodChange}
                  disabled={!canEdit || !isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Instantaneous">Instantaneous</SelectItem>
                    <SelectItem value="AvgByPass">Average Pass</SelectItem>
                    <SelectItem value="AvgByZone">Average Zone</SelectItem>
                    <SelectItem value="AvgByTilt">Average Tilt</SelectItem>
                  </SelectContent>
                </Select>
                )}
              </div>

              {/* Calculation Level */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Calculation Level</Label>
                {isManualJobSelected ? (
                  <div className="flex h-9 items-center rounded-md border border-[#E2E5EA] bg-page-bg px-3 text-[12px] font-medium text-blueprint-blue">
                    Pass Level
                  </div>
                ) : (
                <Select
                  value={calculationLevel}
                  onValueChange={handleCalculationLevelChange}
                  disabled={!canEdit || !isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass Level">Pass Level</SelectItem>
                    <SelectItem value="Zone Level">Zone Level</SelectItem>
                  </SelectContent>
                </Select>
                )}
              </div>

              {/* Slope Time + Minimum Weld Time */}
              {isManualJobSelected ? (
                <ManualWeldingSettingsFields
                  slopeTimeIn={manualSlopeTimeIn}
                  slopeTimeOut={manualSlopeTimeOut}
                  minimumWeldTime={manualMinimumWeldTime}
                  disabled={!canEdit}
                  onSlopeTimeInChange={manualControlledSettings.onSlopeTimeInChange}
                  onSlopeTimeOutChange={manualControlledSettings.onSlopeTimeOutChange}
                  onMinimumWeldTimeChange={manualControlledSettings.onMinimumWeldTimeChange}
                />
              ) : (
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Slope Time (sec)</Label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[11px] text-heritage-grey">In</span>
                    <Input
                      id="slopeTimeIn"
                      type="number"
                      value={slopeTimeIn}
                      onChange={(e) => handleSlopeTimeInChange(e.target.value)}
                      className="text-center text-[12px] h-9"
                      placeholder="0"
                      disabled={!canEdit || !isEditing}
                      min="-10"
                      max="10"
                      step="0.1"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[11px] text-heritage-grey">Out</span>
                    <Input
                      id="slopeTimeOut"
                      type="number"
                      value={slopeTimeOut}
                      onChange={(e) => handleSlopeTimeOutChange(e.target.value)}
                      className="text-center text-[12px] h-9"
                      placeholder="0"
                      disabled={!canEdit || !isEditing}
                      min="-10"
                      max="10"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
              )}

            </div>
          </CardContent>
        </Card>

        {manualWeldingMachines.length > 0 && (
          <div className={isManualJobSelected ? undefined : 'hidden'} aria-hidden={!isManualJobSelected}>
            <ManualStatusConfiguration
              ref={manualConfigurationRef}
              project={project}
              canEdit={canEdit}
              isModalOpen={Boolean(isModalOpen)}
              triggerSave={isManualJobSelected ? manualSaveTrigger : false}
              onSaveComplete={handleManualSaveComplete}
              onValidationFailure={onValidationFailure}
              onValidationErrorsChange={isManualJobSelected ? onValidationErrorsChange : undefined}
              onDirtyChange={isManualJobSelected ? setManualHasModifications : undefined}
              onEnabledParameterKeysChange={isManualJobSelected ? onEnabledParameterKeysChange : undefined}
              hideJobSelector
              hideSettingsCard
              controlledSettings={manualControlledSettings}
              onSettingsSync={handleManualSettingsSync}
              controlledJobNumber={
                isManualJobSelected || pendingManualJobNumbers.includes(selectedJobNumber)
                  ? selectedJobNumber
                  : undefined
              }
              openAddJobRequest={requestManualAddJob}
              onAddJobRequestHandled={() => setRequestManualAddJob(false)}
              onManualJobAdded={handleManualJobAdded}
              limitsData={limitsData}
              limitsLoading={limitsLoading}
              onRequestLimitsReload={loadLimitsData}
            />
          </div>
        )}
        {!isManualJobSelected && (
        /* Status Configuration Table */
        <Card className="border border-[#E2E5EA] shadow-sm max-w-full overflow-hidden" style={{ maxWidth: '100%' }}>
          <CardContent className="p-0 max-w-full overflow-hidden" style={{ maxWidth: '100%' }}>
            <div className="px-4 py-3 bg-page-bg border-b border-[#E2E5EA]">
              <h3 className="text-[14px] font-semibold text-blueprint-blue">Status Configuration Parameters</h3>
            </div>
            {/* Table Section */}
            <div className="flex flex-col w-full max-w-full overflow-hidden">
              <div className="w-full overflow-x-auto overflow-y-visible table-scroll" style={{ maxWidth: '100%' }}>
                <div className="inline-flex min-w-max">
                  {/* Fixed columns */}
                  <div className="sticky left-0 z-20 bg-white shadow-lg">
                    <table className="border-collapse h-full">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          <th className="w-[220px] sm:w-[260px] px-2 sm:px-4 text-left text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden xs:inline">Parameter</span>
                              <span className="xs:hidden">Param</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, index) => (
                          <tr key={param.id} className={`h-[48px] sm:h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'} ${index < parameters.length - 1 ? 'border-b border-[#EEF0F3]' : ''}`}>
                            <td className="w-[220px] sm:w-[260px] px-2 sm:px-4 text-left border-r border-[#E2E5EA]">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <Checkbox
                                  id={`checkbox-${param.id}`}
                                  checked={param.enabled}
                                  onCheckedChange={(checked) => handleParameterChange(param.id, 'enabled', checked)}
                                  disabled={!canEdit || !isEditing}
                                  className="data-[state=checked]:bg-hero-red data-[state=checked]:border-hero-red data-[state=checked]:text-white h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0"
                                />
                                <span className="text-[12px] text-text-grey leading-tight">{param.parameter}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Scrollable parameters section */}
                  <div className="overflow-x-auto flex-1 min-w-0 w-full" style={{ maxWidth: 'calc(100vw - 260px)' }}>
                    <table className="border-collapse h-full min-w-max">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          {uniquePassGroups.map((passName, index) => (
                            <th key={passName} className={`px-1 xs:px-2 sm:px-4 min-w-[140px] xs:min-w-[160px] sm:min-w-[180px] md:min-w-[240px] ${index === 0 ? '' : 'border-l border-[#E2E5EA]'}`}>
                              <div className="h-[28px] sm:h-[36px] flex items-center justify-center text-center text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                                <div className="flex items-center gap-1">
                                  <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                                  <span className="truncate">{passName}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-[10px] font-bold uppercase tracking-wider text-center text-heritage-grey">
                                <div className="py-1">Min</div>
                                <div className="py-1">Max</div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, rowIndex) => (
                          <tr key={`${param.id}-params`} className={`h-[48px] sm:h-[52px] ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'} ${rowIndex < parameters.length - 1 ? 'border-b border-[#EEF0F3]' : ''}`}>
                            {uniquePassGroups.map((passGroup, colIndex) => {
                              const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                              const minKey = `${passGroupKey}Min`;
                              const maxKey = `${passGroupKey}Max`;

                              // Helper function to check if value should be displayed (not 0, '0', null, undefined, or empty)
                              const shouldDisplayValue = (value: any): boolean => {
                                return value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0';
                              };

                              const minValue = shouldDisplayValue(param[minKey]) ? String(param[minKey]) : '';
                              const maxValue = shouldDisplayValue(param[maxKey]) ? String(param[maxKey]) : '';

                              return (
                                <td key={passGroup} className={`px-1 xs:px-2 sm:px-4 py-1 sm:py-2 h-[48px] sm:h-[52px] text-xs ${colIndex === 0 ? '' : 'border-l border-[#E2E5EA]'}`}>
                                  <div className="grid grid-cols-2 gap-1 sm:gap-3 h-full">
                                    <div className="flex items-center justify-center h-full">
                                      <Input
                                        type="number"
                                        value={minValue}
                                        onChange={(e) => handleParameterChange(param.id, minKey, e.target.value)}
                                        disabled={!canEdit || !isEditing || !param.enabled}
                                        className={`w-full text-center text-xs sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${hasInputError(param.parameter, passGroup, 'min')
                                            ? ' focus:ring-red-500 bg-red-50'
                                            : 'border-[#E2E5EA] focus:ring-hero-red/30'
                                          }`}
                                        step="0.01"
                                        placeholder="Min"
                                      />
                                    </div>
                                    <div className="flex items-center justify-center h-full">
                                      <Input
                                        type="number"
                                        value={maxValue}
                                        onChange={(e) => handleParameterChange(param.id, maxKey, e.target.value)}
                                        disabled={!canEdit || !isEditing || !param.enabled}
                                        className={`w-full text-center text-xs sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${hasInputError(param.parameter, passGroup, 'max')
                                            ? ' focus:ring-red-500 bg-red-50'
                                            : 'border-[#E2E5EA] focus:ring-hero-red/30'
                                          }`}
                                        step="0.01"
                                        placeholder="Max"
                                      />
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </>
  );
};